import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Rotas } from 'routes/rotas'
import { BbLogoIcon } from 'components/Custom/IconesSVG'
import { Box, CloseIcon, Typography } from 'components/Material/index'
import { DialogMessageAlert } from '../DialogMessage'
import useCarrinhoStore from 'store/useCarrinho'
import { SpeedDial, SpeedDialAction } from '@mui/material'
import colors from 'colors'
import useTextStyles from 'styles/useTextStyles'
import useResetAllStores from 'hooks/useResetAllStores'
import useSidebarStore from 'store/useSidebar'
import { useMutation } from '@tanstack/react-query'
import { putSalvarNomeCarrinho } from 'services/simulacao/carrinho'
import { ErrorResponse, HandlerErrorMessage } from 'utils/HandlerErrorMessage'
import useSimulationStore from 'store/useSimulation'
import useGlobalStore from 'store/useGlobal'
import generateUniqueName from 'utils/generateUniqueName'
import { AxiosError } from 'axios'
import { useSnackbarAlertContext } from 'contexts/SnackbarContext'

type SelectedItem =
  | 'flexibilization'
  | 'document_aproval'
  | 'suport_material'
  | 'management'
  | 'simulation'
  | 'publicidade_e_midia'
  | 'users'

interface Action {
  name: SelectedItem
  label: string
}

export const FloatingActionButton = () => {
  const navigate = useNavigate()
  const [showDialogMessageAlertOpen, setShowDialogMessageAlertOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<SelectedItem | undefined>(undefined)
  const { cd_inscricao_nacional_app } = useSimulationStore((state) => state)

  const actions: Action[] = [
    // { name: 'users', label: 'Usuários' },
    { name: 'publicidade_e_midia', label: 'Publicidade' },

    { name: 'flexibilization', label: 'Flexibilização' },
    { name: 'document_aproval', label: 'Documentações' },
    { name: 'suport_material', label: 'Material de apoio' },
    { name: 'management', label: 'Gestão de vendas' },
    { name: 'simulation', label: 'Simulação' }
  ]

  const [open, setOpen] = useState(false)
  const handleOpen = () => setOpen(!open)
  const handleClose = () => setOpen(false)
  const { carrinho_tamanho, savedCartName } = useCarrinhoStore((state) => state)
  const resetAllStores = useResetAllStores()
  const { guid } = useGlobalStore((state) => state)
  const { showAlert } = useSnackbarAlertContext()

  function handleRedirect(value: SelectedItem) {
    setSelectedItem(value)
    try {
      if (carrinho_tamanho !== 0 && savedCartName.length === 0) {
        setShowDialogMessageAlertOpen(true)
      } else {
        setShowDialogMessageAlertOpen(false)
        handleOpenItem(value)
      }
    } catch (e) {
      setShowDialogMessageAlertOpen(false)
      console.log(e)
    }
  }

  const salvarNomeCarrinhoMutation = useMutation({
    mutationFn: putSalvarNomeCarrinho,
    onError: (error: AxiosError<ErrorResponse>) => {
      HandlerErrorMessage({ error, showAlert })
    }
  })

  async function handleRedirectSaving() {
    if (!selectedItem) return // Ensure selectedItem is defined
    const cartName = generateUniqueName(cd_inscricao_nacional_app)
    await salvarNomeCarrinhoMutation.mutateAsync({
      nome_carrinho: cartName,
      guid: guid
    })
    handleOpenItem(selectedItem)
  }

  function handleOpenItem(selectedItem: SelectedItem) {
    resetAllStores()
    switch (selectedItem) {
      case 'simulation':
        navigate(Rotas.INICIO)
        break
      case 'management':
        navigate(Rotas.DASHBOARD_MANAGEMENT)
        break
      case 'suport_material':
        navigate(Rotas.MENU)
        setTimeout(() => {
          useSidebarStore.getState().openSidebar('menu-support')
        }, 100)
        break
      case 'document_aproval':
        navigate(Rotas.DOCUMENTACAO)
        break
      case 'flexibilization':
        navigate(Rotas.FLEXIBILIZACAO)
        break
      case 'publicidade_e_midia':
        navigate(Rotas.PUBLICIDADE)
        break
      // case 'users':
      //   navigate(Rotas.USUARIOS)
      //   break
      default:
        break
    }
  }

  return (
    <>
      <SpeedDial
        ariaLabel="Banco do Brasil logo menu"
        sx={{
          position: 'absolute',
          bottom: 15,
          right: 15,
          alignItems: 'flex-end',
          '& .MuiSpeedDial-actions': {
            alignItems: 'flex-end'
          },
          zIndex: 9999
        }}
        icon={
          open ? (
            <Box title="Fechar">
              <CloseIcon sx={{ color: colors['blue-bb'] }} />
            </Box>
          ) : (
            <Box title="Menu Flutuante">
              <BbLogoIcon colors={colors['blue-bb']} />
            </Box>
          )
        }
        onClose={handleClose}
        onClick={() => handleOpen()}
        open={open}
        TransitionProps={{
          style: {
            backgroundColor: !open ? '#FCF800' : '#FFFBFE'
          }
        }}
        FabProps={{
          sx: {
            width: '56px',
            height: '56px',
            backgroundColor: !open ? '#FCF800' : '#FFFBFE',
            borderRadius: '16px',
            boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        {actions.map((action) => (
          <SpeedDialAction
            data-testid={`floating-action-button-${action.name}`}
            key={action.name}
            sx={{ alignItems: 'flex-end' }}
            onClick={() => {
              handleRedirect(action.name)
            }}
            FabProps={{
              sx: {
                fontSize: '14px',
                paddingX: '24px',
                paddingY: '10px',
                boxShadow:
                  '0px 1px 2px 0px rgba(0, 0, 0, 0.30), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                marginBottom: '8px',
                backgroundColor: '#1C1B1F',
                borderRadius: '100px',
                color: '#D0BCFF',
                whiteSpace: 'nowrap',
                display: 'flex',
                width: 'fit-content',
                textTransform: 'revert-layer'
              }
            }}
            icon={
              <Typography sx={{ ...useTextStyles.labelLarge, color: '#D0BCFF' }}>
                {action.label}
              </Typography>
            }
          />
        ))}
      </SpeedDial>
      {showDialogMessageAlertOpen && carrinho_tamanho !== 0 && (
        <DialogMessageAlert
          title="A simulação será perdida"
          message="Os itens adicionados ao carrinho da simulação serão perdidos."
          handleCloseDialog={() => setShowDialogMessageAlertOpen(false)}
          isDialogOpen={showDialogMessageAlertOpen}
          butonName1="Salvar carrinho"
          onClickButton1={() => handleRedirectSaving()}
          buttonName2="Continuar"
          onClickButton2={() => {
            if (selectedItem) {
              handleOpenItem(selectedItem)
            }
          }}
        />
      )}
    </>
  )
}
